import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bookmark, Home, Library, Sun, Moon } from 'lucide-react';
import { useTheme } from '../lib/theme';

export default function Layout({ children }) {
  const loc = useLocation();
  const isActive = (path) => (path === '/' ? loc.pathname === '/' : loc.pathname.startsWith(path));

  return (
    <div className="solveit-grain relative bg-[#0b0f1a] text-slate-100">
      {/* Ambient backdrop */}
      <div className="pointer-events-none fixed inset-0 -z-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[44rem] h-[44rem] rounded-full bg-indigo-600/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[40rem] h-[40rem] rounded-full bg-violet-600/10 blur-3xl" />
      </div>

      <header className="relative z-20 sticky top-0 backdrop-blur-xl bg-[#0b0f1a]/70 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2.5 sm:gap-3 group min-w-0 flex-shrink">
            <motion.div
              initial={{ rotate: -8, scale: 0.9 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ type: 'spring', stiffness: 240, damping: 18 }}
              className="shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-400/90 to-violet-500/90 flex items-center justify-center ring-1 ring-white/10 shadow-[0_0_24px_-8px_rgba(129,140,248,0.7)]"
            >
              <Library className="w-[18px] h-[18px] text-white" strokeWidth={2.2} />
            </motion.div>
            <div className="flex flex-col justify-center min-w-0">
              <div className="font-semibold text-[15px] leading-[1.15] tracking-tight text-slate-100">ThetaLabs</div>
              <div className="hidden sm:block text-[10.5px] mt-[3px] leading-none text-slate-500 font-medium tracking-[0.12em] uppercase truncate">
                JEE Advanced · PYQ Companion
              </div>
            </div>
          </Link>

          <nav className="flex items-center gap-0.5 sm:gap-1 shrink-0">
            <NavLink to="/" active={isActive('/')} icon={<Home className="w-4 h-4" />} label="Library" />
            <NavLink to="/bookmarks" active={isActive('/bookmarks')} icon={<Bookmark className="w-4 h-4" />} label="Saved" />
            <ThemeToggle />
          </nav>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 py-10">{children}</main>
    </div>
  );
}

function NavLink({ to, active, icon, label }) {
  return (
    <Link to={to} className="relative px-2 sm:px-3 py-1.5 rounded-lg text-sm font-medium transition-colors" aria-label={label} title={label}>
      <span className={`relative z-10 flex items-center gap-1.5 ${active ? 'text-slate-100' : 'text-slate-400 hover:text-slate-200'}`}>
        {icon}
        <span className="hidden sm:inline">{label}</span>
      </span>
      {active && (
        <motion.span
          layoutId="nav-pill"
          transition={{ type: 'spring', stiffness: 380, damping: 30 }}
          className="absolute inset-0 rounded-lg bg-white/[0.06] ring-1 ring-white/10"
        />
      )}
    </Link>
  );
}

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const isDark = theme === 'dark';
  const Icon = isDark ? Sun : Moon;
  return (
    <button
      onClick={toggle}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      className="relative ml-1 w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-100 bg-white/[0.03] hover:bg-white/[0.08] ring-1 ring-white/5 transition-colors"
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={theme}
          initial={{ rotate: -90, opacity: 0, scale: 0.85 }}
          animate={{ rotate: 0, opacity: 1, scale: 1 }}
          exit={{ rotate: 90, opacity: 0, scale: 0.85 }}
          transition={{ type: 'spring', stiffness: 380, damping: 28 }}
          className="flex"
        >
          <Icon className="w-4 h-4" />
        </motion.span>
      </AnimatePresence>
    </button>
  );
}
